import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import { getBreadcrumbSchema } from '../../utils/structuredData';
import { getImageUrl } from '../../utils/seo';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.9s ease ${delay}s, transform 0.9s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

const principles = [
  {
    label: 'I',
    title: 'Biçim amaç değildir.',
    sub: 'Kararın kaçınılmaz sonucudur.',
  },
  {
    label: 'II',
    title: 'Mimarlık nötr değildir.',
    sub: 'Biz de değiliz.',
  },
  {
    label: 'III',
    title: 'Geometri stil değildir.',
    sub: 'Tavırdır.',
  },
  {
    label: 'IV',
    title: 'SAM bir imza değildir.',
    sub: 'Bir duruştur.',
  },
];

const chapters = [
  {
    num: 'I',
    title: 'TEMASSIZLIK İLKESİ',
    paragraphs: [
      'Temassızlık, karardır.',
      'Gravitas Libera\'da kütleler birbirine dokunmaz. Mobilya zeminden kopar, duvar nefes alır, tavan yük taşımaz.',
      'Bu kopuş estetik değil, zihinseldir. Temas azaldıkça, zihin gereksiz yerlere sapmaz. Mekân, kullanıcıdan bir şey talep etmez.',
      'Sadece karar vermesi gereken yüzeyi bırakır.',
    ],
    image: 'https://readdy.ai/api/search-image?query=minimalist%20architectural%20interior%20floating%20furniture%20concrete%20walls%20dramatic%20shadows%20geometric%20precision%20warm%20ambient%20light%20ultra%20clean%20sophisticated%20atmosphere%20high%20end%20design%20studio%20photography&width=900&height=700&seq=gl-chapter-1&orientation=landscape',
  },
  {
    num: 'II',
    title: 'MEKÂNIN AĞIRLIĞINI HAFİFLETMEK',
    paragraphs: [
      'Gravitas Libera, yerçekimiyle değil; ışık ve gölgeyle çalışır.',
      'Ağır olan gizlenir. Görünür olan hafifletilir. Boşluk, eksiklik değil; zihinsel nefes alanıdır.',
      'Görsel ağırlık azaldıkça, düşünce netleşir. Netlik hız getirir. Hız, otorite üretir.',
    ],
    image: 'https://readdy.ai/api/search-image?query=minimalist%20interior%20space%20natural%20light%20rays%20through%20narrow%20window%20concrete%20texture%20shadow%20play%20warm%20tones%20serene%20atmosphere%20architectural%20photography%20high%20end%20design&width=900&height=700&seq=gl-chapter-2&orientation=landscape',
  },
  {
    num: 'III',
    title: 'MESAFE VE DİSİPLİN',
    paragraphs: [
      'Her detay, bu felsefenin somutlaşmış hâlidir.',
      'Masalar tek bir karar yüzeyine indirgenir. Tüm operasyonel yük gizlenir. Tek temas noktası, düşüncenin odaklandığı yerdir.',
      'Disiplin, mekânda bağırmaz. Sessizce hüküm sürer.',
    ],
    image: 'https://readdy.ai/api/search-image?query=minimalist%20desk%20workspace%20single%20surface%20clean%20lines%20warm%20concrete%20walls%20precise%20geometry%20disciplined%20interior%20design%20warm%20ambient%20lighting%20sophisticated%20atmosphere&width=900&height=700&seq=gl-chapter-3&orientation=landscape',
  },
  {
    num: 'IV',
    title: 'GRAVITAS LIBERA FELSEFESİ',
    paragraphs: [
      'Ağır olan sadece işlevdir. Görünür olan hafifliktir.',
      'Mekân sahibini kontrol etmez. Sahibini özgürleştirir.',
      'Gravitas Libera bir stil değil; zihinsel otoritenin mekânsal ifadesidir.',
    ],
    image: 'https://readdy.ai/api/search-image?query=minimalist%20luxury%20interior%20space%20open%20plan%20warm%20tones%20concrete%20stone%20natural%20materials%20serene%20atmosphere%20freedom%20spatial%20design%20high%20end%20architecture%20photography&width=900&height=700&seq=gl-chapter-4&orientation=landscape',
  },
];

export default function GravitasLibera() {
  const breadcrumbItems = [
    { name: 'Anasayfa', url: '/' },
    { name: 'Gravitas Libera', url: '/gravitas-libera' }
  ];

  return (
    <div className="bg-[#0e0c0a] min-h-screen">
      <SEOHead
        title="Gravitas Libera | Manifesto | Studio Asım Mete"
        description="Gravitas Libera - Zihinsel gürültüyü sıfıra indirir. Studio Asım Mete'nin tasarım felsefesi ve manifesto. Mekânsal formda sessiz otorite."
        keywords="gravitas libera, studio asım mete manifesto, tasarım felsefesi, minimalist mimari, mekansal otorite, iç mimari felsefe"
        canonical="/gravitas-libera"
        ogType="article"
        structuredData={getBreadcrumbSchema(breadcrumbItems)}
      />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=abstract%20minimalist%20architectural%20space%20dark%20warm%20tones%20concrete%20geometry%20dramatic%20shadows%20sophisticated%20atmosphere%20ultra%20high%20end%20design%20studio%20photography%20cinematic&width=1920&height=1080&seq=gl-hero-bg&orientation=landscape')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0e0c0a]/75 via-[#0e0c0a]/60 to-[#0e0c0a]/90"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-6 w-full mx-auto">
          <p className="text-xs tracking-[0.4em] text-white uppercase mb-10 font-sans">Studio Asım Mete — Manifesto</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl 2xl:text-9xl font-light text-white tracking-[0.15em] mb-10 leading-none font-serif whitespace-nowrap">
            GRAVITAS LIBERA
          </h1>
          <div className="w-16 h-px bg-[#c8b89a]/50 mx-auto mb-10"></div>
          <p className="text-xl md:text-2xl text-white/80 font-light leading-relaxed font-serif">
            Zihinsel gürültüyü sıfıra indirir.
          </p>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
          <span className="text-[10px] tracking-[0.3em] text-white uppercase font-sans">Aşağı Kaydır</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#6b5a45] to-transparent animate-pulse"></div>
        </div>
      </section>

      {/* ── GİRİŞ — ANA TEZ ── */}
      <section className="py-36 px-6 bg-[#0e0c0a]">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <p className="text-[10px] tracking-[0.5em] text-white uppercase mb-12 font-sans">GİRİŞ — ANA TEZ</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-2xl md:text-3xl text-white font-light leading-[1.9] mb-6 font-serif">
              Gravitas Libera, zihinsel gürültüyü sıfıra indirir.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-xl md:text-2xl text-white font-light leading-[2] mb-4 font-serif">
              Mekân, kullanım alanı değildir.
            </p>
          </FadeIn>
          <FadeIn delay={0.3}>
            <p className="text-xl md:text-2xl text-white font-light leading-[2] mb-4 font-serif">
              Karar kapasitesini artıran bir araçtır.
            </p>
          </FadeIn>
          <FadeIn delay={0.4}>
            <p className="text-xl md:text-2xl text-white font-light leading-[2] mb-4 font-serif">
              Fiziksel yük azaldıkça, zihinsel netlik artar.
            </p>
          </FadeIn>
          <FadeIn delay={0.5}>
            <p className="text-2xl md:text-3xl text-white font-light leading-[2] font-serif">
              Netlik, otorite üretir.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ── AYIRICI ── */}
      <div className="max-w-5xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-[#3d3020] to-transparent"></div>
      </div>

      {/* ── PRENSİPLER ── */}
      <section className="py-36 px-6 bg-[#0e0c0a]">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-[10px] tracking-[0.5em] text-white uppercase mb-20 text-center font-sans">PRENSİPLER</p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {principles.map((p, i) => (
              <FadeIn key={i} delay={i * 0.12} className="border border-[#1e1a14] p-12 flex flex-col justify-between min-h-[220px] hover:bg-[#1c1612]/40 transition-colors duration-500">
                <span className="text-[10px] tracking-[0.4em] text-white uppercase font-sans mb-6">{p.label}</span>
                <div>
                  <p className="text-2xl md:text-3xl text-white font-light leading-snug mb-3 font-serif">{p.title}</p>
                  <p className="text-xl md:text-2xl text-white font-light font-serif">{p.sub}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ── BÖLÜMLER ── */}
      {chapters.map((ch, i) => (
        <section
          key={i}
          className={`py-32 px-6 ${i % 2 === 0 ? 'bg-[#1c1612]' : 'bg-[#0e0c0a]'}`}
        >
          <div className="max-w-6xl mx-auto">
            <div className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-16 items-center`}>
              {/* Görsel */}
              <FadeIn delay={0.1} className="w-full md:w-1/2">
                <div className="relative w-full h-[420px] overflow-hidden">
                  <img
                    src={getImageUrl(ch.image)}
                    alt={ch.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e0c0a]/40 to-transparent"></div>
                </div>
              </FadeIn>

              {/* Metin */}
              <div className="w-full md:w-1/2 space-y-6">
                <FadeIn>
                  <p className="text-[10px] tracking-[0.5em] text-white uppercase font-sans mb-2">{ch.num}</p>
                  <h2 className="text-2xl md:text-3xl font-light text-white tracking-widest mb-8 leading-snug font-serif">
                    {ch.title}
                  </h2>
                </FadeIn>
                {ch.paragraphs.map((para, j) => (
                  <FadeIn key={j} delay={j * 0.1}>
                    <p className={`leading-[2] font-light font-serif ${j === 0 ? 'text-xl text-white' : 'text-lg text-white'}`}>
                      {para}
                    </p>
                  </FadeIn>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* ── KAPANIŞ ── */}
      <section className="relative py-48 px-6 overflow-hidden bg-[#0e0c0a]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=minimalist%20architectural%20space%20warm%20concrete%20texture%20abstract%20geometry%20dark%20tones%20sophisticated%20atmosphere%20high%20end%20design%20photography&width=1920&height=800&seq=gl-closing-bg&orientation=landscape')`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0e0c0a] via-transparent to-[#0e0c0a]"></div>

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <FadeIn>
            <div className="w-px h-20 bg-gradient-to-b from-transparent via-[#c8b89a]/40 to-transparent mx-auto mb-16"></div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="text-3xl md:text-5xl text-white font-light leading-[1.7] mb-6 font-serif">
              Gravitas Libera bir stil değil;
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="text-3xl md:text-5xl text-white font-light leading-[1.7] mb-16 font-serif">
              zihinsel otoritenin mekânsal ifadesidir.
            </p>
          </FadeIn>
          <FadeIn delay={0.35}>
            <div className="w-16 h-px bg-[#c8b89a]/40 mx-auto mb-16"></div>
            <Link
              to="/iletisim"
              className="inline-block border border-[#c8b89a]/40 text-white text-xs tracking-[0.4em] uppercase px-10 py-4 hover:bg-[#c8b89a]/10 transition-all duration-300 cursor-pointer whitespace-nowrap font-sans"
            >
              Bir Projeniz mi Var?
            </Link>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
